import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "I broke AJAX in Chrome 52 ?",
  "description": "",
  "published": "2016-08-11T00:00:00.000Z",
  "redirect_from": ["/blog/i-broke-ajax-in-chrome-52-%f0%9f%98%b1/swizec/6914"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "https://swizec.com/blog/js-object-optimization-bug-in-chrome-52/swizec/6908"
      }}>{`This strange bug from yesterday`}</a>{` won’t let me sleep. `}<em parentName="p">{`Why`}</em>{` does Chrome 52 sometimes behave like different instances of a class are the same object?`}</p>
    <p>{`Backbone’s fetch method triggers the bug. But maybe that’s not the real issue? I’d hate to submit a bug report to Chrome project only to be told `}<em parentName="p">{`“Fool, that’s a Backbone issue.”`}</em></p>
    <p>{`It’s terrifying to tell the Chrome team they made a mistake.`}</p>
    <p>{`This is what yesterday’s repro code looks like:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var BugModel = Backbone.Model.extend({
  url: "bla.json",
});

let bug = new BugModel();
bug.fetch({
  success: () => {
    console.log("fetch 1"); // prints
    doWeirdness(bug);
  },
});

function doWeirdness(bug) {
  let newBug = new BugModel({ id: 1 });

  console.log("about to re-fetch"); // prings

  newBug.fetch({
    success: () => console.log("fetch 2", newBug), // doesn't print
    error: () => console.log("error"),
  });
  newBug.fetch({
    success: () => console.log("fetch 3"), // prints
  });
}
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/chrome-eats-objects.gif",
        "alt": "2 fetches, 1 callback"
      }}></img></p>
    <p>{`If you press Cmd+R, the bug happens. If you press Cmd+Shift+R, it does not. That’s a new clue that points at either Chrome’s speed optimizations, or worse, the network stack. Can we call it a network stack? I guess Chrome `}<em parentName="p">{`is`}</em>{` almost an operating system at this point …`}</p>
    <p>{`Adding `}<inlineCode parentName="p">{`console.log(newBug == bug)`}</inlineCode>{` prints `}<inlineCode parentName="p">{`false`}</inlineCode>{`, which implies that Chrome does `}<em parentName="p">{`not`}</em>{` think both instances are the same object. This invalidates my original hypothesis. ?`}</p>
    <p>{`So what does Backbone’s `}<inlineCode parentName="p">{`Model.fetch`}</inlineCode>{` method do?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`fetch: function(options) {
      options = _.extend({parse: true}, options);
      var model = this;
      var success = options.success;
      options.success = function(resp) {
        var serverAttrs = options.parse ? model.parse(resp, options) : resp;
        if (!model.set(serverAttrs, options)) return false;
        if (success) success.call(options.context, model, resp, options);
        model.trigger('sync', model, resp, options);
      };
      wrapError(this, options);
      return this.sync('read', this, options);
    },
`}</code></pre>
    <p>{`A lot of this stuff is unnecessary in ES6, but Backbone is from the before times.`}</p>
    <p>{`We start with a default value for options - `}<inlineCode parentName="p">{`{parse: true}`}</inlineCode>{`, then use the `}<inlineCode parentName="p">{`var = this`}</inlineCode>{` trick because we don’t have arrow functions. Then we copy the `}<inlineCode parentName="p">{`options.success`}</inlineCode>{` callback to a variable and define our own. You can think of it as a wrapper.`}</p>
    <p>{`Inside the success wrapper, we parse data returned from the server and set new values on our model. Then we trigger a `}<inlineCode parentName="p">{`sync`}</inlineCode>{` event. This could be where the bug happens.`}</p>
    <p>{`Outside the wrapper, we defer to `}<inlineCode parentName="p">{`sync`}</inlineCode>{` to actually talk to the server.`}</p>
    <p>{`If I copy this method to my own model definition, we can inspect where it fails.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/success-wrapper.gif",
        "alt": "Success wrapper doesn't fire"
      }}></img></p>
    <p>{`The success wrapper doesn’t fire. ?`}</p>
    <p>{`Let’s see what happens inside `}<inlineCode parentName="p">{`sync`}</inlineCode>{` … ugh, it’s a long function. I’m not pasting it here. It does some setup, then defers to `}<inlineCode parentName="p">{`$.ajax`}</inlineCode>{` to perform an ajax request to the server.`}</p>
    <p>{`Can we make the same bug happen without Backbone, then?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`$.ajax({
  url: "bla.json",
  complete: () => {
    console.log("done 1st request");
    $.ajax({
      url: "bla.json",
      complete: () => {
        console.log("done 2nd request");
      },
    });
  },
});
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/purer-bug.gif",
        "alt": "Bug without Backbone"
      }}></img></p>
    <p>{`? It worked! 8 lines of code reproduce the bug. ?`}</p>
    <p>{`And yes, both requests happen without error.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/bug-network.gif",
        "alt": "Network calls do happen"
      }}></img></p>
    <p>{`It might be safe to say that jQuery is battle tested enough that this couldn’t be a jQuery bug. But let’s try superagent to make sure. It’s a great library for making requests and it’s implemented independently of jQuery.`}</p>
    <p>{`Does the bug still happen?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`request.get("bla.json").end(() => {
  console.log("1st success");
  request.get("bla.json").end(() => {
    console.log("2nd success");
  });
});
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/superagent-bug.gif",
        "alt": "Superagent repros too"
      }}></img></p>
    <p>{`Yeeeeep.`}</p>
    <p>{`Now you might think: `}<em parentName="p">{`“A-ha! Every even Ajax call to the same URL fails.”`}</em>{` I tried that, too -> it doesn’t. If you make requests in a loop, they all work.`}</p>
    <p>{`The bug only happens, if you make the same AJAX request in the callback. You can extend a chain like this forever:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`request.get("bla.json").end(() => {
  console.log("1st success");
  request.get("bla.json").end(() => {
    console.log("2nd success");
  });

  request.get("bla.json").end(() => {
    console.log("3rd success");

    request.get("bla.json").end(() => {
      console.log("4th success");
    });
  });
});
`}</code></pre>
    <p>{`And it only prints the odd numbered console.logs.`}</p>
    <p>{`Guess it’s time to submit my first bug report to a big open source project. Yay I’m helping!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      